import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import './Layout.css';

interface LayoutProps {
  children: React.ReactNode;
}

// ホバー画像のコンテキスト
const HoverImageContext = createContext<{
  setHoveredImage: (image: string | null) => void;
}>({
  setHoveredImage: () => {}
});

export const useHoverImage = () => useContext(HoverImageContext);

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [hoveredImage, setHoveredImage] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  // モバイル判定
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Lenis慣性スクロール実装（一元管理）
  useEffect(() => {
    // モバイルかどうかを判定
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile) {
      // モバイルの場合はLenisを使わず、ネイティブスクロールを使用
      console.log('📱 Mobile detected - using native scroll');
      return;
    }

    // PC の場合のみLenisを有効化
    import('@studio-freight/lenis').then(({ default: Lenis }) => {
      const wrapper = document.querySelector('.content-window');
      const content = document.querySelector('.scroll-content');

      if (!wrapper || !content) {
        console.warn('⚠️ Lenis: wrapper or content not found');
        return;
      }

      console.log('✅ Initializing Lenis on .content-window (Layout - PC only)');

      const lenis = new Lenis({
        wrapper: wrapper as HTMLElement,
        content: content as HTMLElement,
        duration: 1.2,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1,
        touchMultiplier: 2,
        infinite: false,
      });

      function raf(time: number) {
        lenis.raf(time);
        requestAnimationFrame(raf);
      }

      requestAnimationFrame(raf);

      console.log('✅ Lenis scroll ENABLED (Layout - PC)');

      // クリーンアップ関数を保存
      (window as any).lenisCleanup = () => {
        lenis.destroy();
        console.log('🧹 Lenis DESTROYED (Layout)');
      };
    });

    return () => {
      if ((window as any).lenisCleanup) {
        (window as any).lenisCleanup();
        (window as any).lenisCleanup = null;
      }
    };
  }, []);

  return (
    <HoverImageContext.Provider value={{ setHoveredImage }}>
      <div className="layout">
        <main className="main-content">
          <div className="content-window" ref={scrollRef}>
            <div className="scroll-content" ref={contentRef}>
              {children}
            </div>
          </div>
        </main>
        {/* ホバー画像を表示（PCのみ） */}
        {!isMobile && hoveredImage && (
          <div className="hover-image-fixed">
            <img src={hoveredImage} alt="Project preview" />
          </div>
        )}
      </div>
    </HoverImageContext.Provider>
  );
};

export default Layout;